// ButtonControl.tsx
import React, { lazy, useState, Suspense, useEffect } from 'react';
import { Control } from '../ControlRenderer';
import { get, set, evaluateTemplate } from '../utils/Utils'
import { Button, IconButton, Tooltip, CircularProgress, Link, MenuItem, Typography } from '@mui/material';
import { FormModeType, useFormData } from '@/components/FormDataProvider';
import IconMap from '../utils/IconMap';
import ModalBox, { Action } from '../ModalBox';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { useConfig } from '@/components/ConfigProvider';
import { useRouter } from 'next/navigation';
import navigateActionEditor from '../../../layouts/actions/NavigateActionEditor.json';
import pagenavActionEditor from '../../../layouts/actions/PageNavActionEditor.json';
import loadformActionEditor from '../../../layouts/actions/LoadFormActionEditor.json';
import messageboxActionEditor from '../../../layouts/actions/MessageBoxActionEditor.json';
import postdataActionEditor from '../../../layouts/actions/PostdataActionEditor.json';
import uploadActionEditor from '../../../layouts/actions/UploadActionEditor.json';
import { useAuth } from '@/components/AuthProvider';

/**
* Represents a button control with specific properties for actions, icons, and styling.
* Extends the base Control interface.
*/
export interface ButtonControl extends Control {
  /**
   * The action to be executed when the button is clicked.
   */
  clickAction: Action;

  /**
   * A string representing the icon to be displayed on the button.
   * This could refer to a CSS class or an icon identifier. (Optional)
   */
  icon?: string;

  /**
   * Defines the font size of the button's label.
   * Allows customization for design and readability. (Optional)
   */
  fontSize?: number;
}

interface ButtonControlProps {
  control: Control;
  data: any;
  mode: FormModeType;
  onComplete?: () => void;
  onError?: () => void;
  onClose?: () => void;
  sx?: SxProps<Theme>;
}

const ButtonControl: React.FC<ButtonControlProps> = ({ control, data, mode, onComplete, onError, onClose, sx }) => {
  const { apiUrl } = useConfig(); // Get the API root URL from config
  const { token } = useAuth(); // Get the bearer token
  const [UIAction, setUIAction] = useState<Action | null>(null);
  const router = useRouter();
  const { formData, setFormDataElement, fetchFormDataElement, fetchLookupData, setFormDataInfo, navigatePage } = useFormData();

  let currentMode = control.mode ? control.mode : mode;

  const buttonControl = control as ButtonControl;

  const handleSuccess = (action: Action) => {
    if (action.successAction && action.successAction.type) {
      handleAction(action.successAction);
    }
    else {
      setUIAction(null);
      if (onComplete)
        onComplete();
    }
  }

  const handleFailure = (action: Action) => {
    if (action.failureAction && action.failureAction.type) {
      handleAction(action.failureAction);
    }
    else {
      setUIAction(null);
      if (onError)
        onError();
    }
  }

  const handleClose = (action: Action) => {
    setUIAction(null);
    if (onClose)
      onClose();
  }

  const getEditorAction = (actiontype: string): Action => {
    let editor;
    // get action designer by type
    switch (actiontype) {
      case 'navigate':
        editor = navigateActionEditor;
        break;
      case 'pagenav':
        editor = pagenavActionEditor;
        break;
      case 'loadform':
        editor = loadformActionEditor;
        break;
      case 'messagebox':
        editor = messageboxActionEditor;
        break;
      case 'postdata':
        editor = postdataActionEditor;
        break;
      case 'postdata2':
        editor = postdataActionEditor;
        break;
      case 'upload':
        editor = uploadActionEditor;
        break;
    }
    return {
      type: 'messagebox',
      payload: {
        title: `Edit Action properties`,
        layout: editor,
        okbutton: true
      }
    };
  }

  const setEditorAction = async (action: Action) => {
    if (!action || !data.type) {
      console.warn('no action type specified');
      return;
    }

    setUIAction(getEditorAction(data.type));
  }

  const handleAction = async (action: Action) => {
    if (!action || !action.type) {
      console.warn('no action specified');
      return;
    }
    console.log(`executing action ${action.type}`);
    switch (action.type) {
      case 'clearformdata':
        if (action.payload && action.payload.dataKey) {
          setFormDataElement(action.payload.dataKey, {});
        }
        handleSuccess(action);
        break;
      case 'fetchformdata':
        if (action.payload && action.payload.dataKey && action.payload.postDataKey !== undefined) {
          fetchFormDataElement(action.payload.dataKey, action.payload.postDataKey, action.payload.resultDataKey);
        }
        handleSuccess(action);
        break;
      case 'fetchlookupdata':
        if (action.payload && action.payload.dataKey) {
          fetchLookupData(action.payload.dataKey, action.payload.postDataKey, action.payload.resultDataKey);
        }
        handleSuccess(action);
        break;
      case 'loadform':
        if (action.payload && action.payload.dataKey && action.payload.formMode) {
          //console.log(data);
          const formKey = get(data, action.payload.dataKey, null);
          if (formKey) {
            setFormDataInfo(formKey, action.payload.formMode);
            // Navigate to the /edit route
            router.push('/edit');
            handleSuccess(action);
          }
          else {
            console.warn("formKey not found");
          }
        }
        handleFailure(action);
        break;
      case 'postdata':
        if (action.payload && action.payload.endpoint) {
          const postData = action.payload.postDataKey ? get(data, action.payload.postDataKey, {}) : data;
          let endpoint = evaluateTemplate(action.payload.endpoint, data, formData);
          let url = `${apiUrl}/${endpoint}`;
          if (action.payload.dataKey) {
            const dataKeyValue = get(data, action.payload.dataKey, '');
            if (dataKeyValue) {
              url += `/${dataKeyValue}`;
            }
          }

          try {
            const response = await fetch(url, {
              method: 'POST',
              mode: 'cors', // Allow cross-origin requests
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
                // Add any additional headers if needed
              },
              body: JSON.stringify(postData)
            });

            if (response.ok) {
              handleSuccess(action);
            } else {
              const errorText = await response.text();
              if (errorText) {
                setFormDataElement("validationResult_resultMessage", errorText);
                setFormDataElement("validationResult_severity", "error");
                console.error(`Failed post data: ${response.status} ${errorText}`);
              }
              handleFailure(action);
            }
          } catch (error) {
            console.error('Error posting data:', error);
            handleFailure(action);
          } finally {
            // start progress indicator
          }
        }
        break;
      case 'postdata2':
        if (action.payload && action.payload.endpoint) {
          const postData = action.payload.postDataKey ? get(data, action.payload.postDataKey, {}) : data;
          let endpoint = evaluateTemplate(action.payload.endpoint, data, formData);
          let url = `${apiUrl}/${endpoint}`;
          if (action.payload.dataKey) {
            const dataKeyValue = get(data, action.payload.dataKey, '');
            if (dataKeyValue) {
              url += `/${dataKeyValue}`;
            }
          }

          try {
            const response = await fetch(url, {
              method: 'POST',
              mode: 'cors', // Allow cross-origin requests
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
                // Add any additional headers if needed
              },
              body: JSON.stringify(postData)
            });

            if (response.ok) {
              if (action.payload.dataKey) {
                const ret = await response.json(); // Attempt to parse JSON
                for (const prop in ret) {
                  if (action.payload.setRoot) {
                    set(formData, prop, ret[prop]);
                  }
                  else if (action.payload.resultDataKey) {
                    setFormDataElement(`${action.payload.resultDataKey}_${prop}`, ret[prop]);
                  } else {
                    set(data, prop, ret[prop]);
                  }
                }
              }
              handleSuccess(action);
            } else {
              const errorText = await response.text();
              if (errorText) {
                setFormDataElement("validationResult_resultMessage", errorText);
                setFormDataElement("validationResult_severity", "error");
                console.error(`Failed post data: ${response.status} ${errorText}`);
              }
              handleFailure(action);
            }
          } catch (error) {
            console.error('Error posting data:', error);
            handleFailure(action);
          } finally {
            // start progress indicator
          }
        }
        break;
      case 'navigate':
        if (action.payload && action.payload.endpoint) {
          let endpoint = evaluateTemplate(action.payload.endpoint, data, formData);
          let url = `${apiUrl}/${endpoint}`;
          if (action.payload.dataKey) {
            const dataKeyValue = get(data, action.payload.dataKey, '');
            if (dataKeyValue) {
              url += `/${dataKeyValue}`;
            }
          }
          window.open(url, action.payload.target ? action.payload.target : '_blank');
          handleSuccess(action);
        } else {
          console.warn('Invalid navigate action payload.');
          handleFailure(action);
        }
        break;
      case 'pagenav':
        if (action.payload && action.payload.page) {
          navigatePage(evaluateTemplate(action.payload.page, data, formData));
          handleSuccess(action);
        } else {
          console.warn('Invalid navigate action page.');
          handleFailure(action);
        }
        break;
      case 'messagebox':
        setUIAction(action);
        break;
      case 'editaction':
        setEditorAction(action);
        break;
      default:
        console.warn(`Invalid action ${action.type}.`);
        break;
    }
  }


  const handleClick = () => {
    handleAction(buttonControl.clickAction);
  };

  const icon = buttonControl.icon;
  const IconComponent = icon ? IconMap[icon] : null;

  return (
    <>
      {control.type === 'link' ? (
        <Link
          component="button"
          variant="body2"
          underline="hover"
          disabled={currentMode === 'view'}
          className={`eform-${control.type}`}
          onClick={handleClick}
          sx={{ fontSize: (control as ButtonControl).fontSize, ...sx }}
        >
          {control.label}
        </Link>
      ) : IconComponent ? (
        <Tooltip title={control.label}>
          <span>
            <Suspense fallback={<CircularProgress size={24} />}>
              <IconButton
                onClick={handleClick}
                disabled={currentMode === 'view'}
                className={`eform-${control.type}`}
                sx={sx}
              >
                <IconComponent />
              </IconButton>
            </Suspense>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant={control.type === 'gridbutton' ? 'outlined' : 'contained'}
          className={`eform-${control.type}`}
          onClick={handleClick}
          disabled={currentMode === 'view'}
          sx={{ fontSize: (control as ButtonControl).fontSize, ...sx }}
        >
          {control.label}
        </Button>
      )}
      {UIAction &&
        <ModalBox
          action={UIAction}
          data={data}
          onDataChange={() => { }}
          mode={mode}
          onComplete={() => handleSuccess(UIAction)}
          onError={() => handleFailure(UIAction)}
          onClose={() => handleClose(UIAction)} />}
    </>
  );
};

export default ButtonControl;